import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/UserStore'
import { useRoute } from 'vue-router'
import { POSTHOG_EVENTS } from '@/constants'
import {
  safeCapture,
  overrideInventoryBeta,
  clearInventoryBetaOverride,
  INVENTORY_BETA_CONTROL,
} from '@/helpers/posthog'

const MS_PER_DAY = 86_400_000

/**
 * Whole days between an ISO timestamp and now.
 *
 * Returns `null` — never `0` — when the timestamp is missing or unparseable, so
 * callers can OMIT the property instead of reporting a confident zero. A beta
 * user enrolled by flag rollout never passed through `setNewDesign(true)`, so
 * their first stint has no recorded start; "unknown" and "same day" must not
 * collapse into the same number in the funnels (CDP-2574).
 */
const wholeDaysSince = (iso) => {
  if (!iso) return null

  const then = Date.parse(iso)

  if (Number.isNaN(then)) return null

  return Math.max(0, Math.floor((Date.now() - then) / MS_PER_DAY))
}

/**
 * One switch per page life. `setNewDesign` ends in a full reload, so a second
 * call can only come from a double-click on the toggle — which would fire the
 * event twice and double-increment the stint. Module scope, not composable
 * scope: each component gets its own `useNewDesign()` instance, and the guard
 * has to hold across all of them.
 */
let switchInFlight = false

/**
 * useNewDesign — reactive gate for the "new design" rollout.
 *
 * Single source of truth is `user.data.about.new_design` (surfaced by the
 * `UserStore.isNewDesign` getter). Everything that belongs to the new design
 * — new views, toolbars, action-slot buttons, layouts — must be gated on this
 * so the whole feature can be flipped from one place.
 *
 *   const { isNewDesign } = useNewDesign()
 *   <NewThing v-if="isNewDesign" />
 *   <OldThing v-else />
 *
 * Reactive: when the toggle flips `user.data.about.new_design`, every consumer
 * updates automatically.
 */
export function useNewDesign () {
  const userStore = useUserStore()
  const { isNewDesign, newDesignPromptDismissed, newDesignPromptDismissedFinal, newDesignTourSeen } = storeToRefs(userStore)
  const route = useRoute()

  // Merge-patch `user.data.about` with the given partial. Spreads existing
  // `data` + `about` so no sibling fields are dropped (the PATCH replaces the
  // whole `data` object server-side).
  const patchAbout = (partial) => {
    const data = userStore.user?.data || {}
    const about = data.about || {}
    return userStore.updateUser({ data: { ...data, about: { ...about, ...partial } } })
  }

  /**
   * Flip the design mode by persisting `user.data.about.new_design`. After it
   * resolves we do a full reload so AG Grid column defs, route guards and layout
   * chrome all re-evaluate cleanly under the new mode.
   */
  const setNewDesign = async (value) => {
    if (switchInFlight) return
    switchInFlight = true

    const optingIn = value === true

    // Stint bookkeeping . A "stint" is one continuous run in the beta;
    // it starts on entry and ends on opt-out. Kept in `data.about` next to
    // `new_design` rather than in localStorage or a PostHog person property,
    // because it has to survive a new device — a dealer who bails on their
    // laptop and returns on the shop iPad is still a return, and losing that
    // undercounts exactly the number the ticket exists to measure.
    const about = userStore.user?.data?.about || {}
    const now = new Date().toISOString()
    const recordedStint = Number.isInteger(about.new_design_stint) ? about.new_design_stint : null

    // `is_return` is decided by evidence of a previous exit, not by the stint
    // counter: a rollout-enrolled user has no counter but can still have opted
    // out once, and that IS a return.
    const isReturn = optingIn && !!about.new_design_opted_out_at
    // Increments on opt-in only. Unknown history reads as stint 1, which is the
    // honest floor: we know of no earlier stint.
    const stintNumber = optingIn
      ? (isReturn ? (recordedStint ?? 1) + 1 : (recordedStint ?? 1))
      : (recordedStint ?? 1)

    // Keep the PostHog experiment in step with the user's choice. Opting out
    // pins them to `control` client-side so they aren't handed the beta again
    // on the next load while still sitting in the test cohort; opting back in
    // drops the pin and lets their real assignment apply.
    // The durable record is `new_design` on the account either way — the
    // override only covers this browser.
    optingIn
      ? clearInventoryBetaOverride()
      : overrideInventoryBeta(INVENTORY_BETA_CONTROL)

    // `reason` is hardcoded for now but stays a property, so a future
    // "why are you leaving?" prompt can populate it without a schema change.
    const eventProps = { reason: 'user_initiated', stint_number: stintNumber, from: route.fullPath }

    if (optingIn) {
      eventProps.is_return = isReturn
      const daysAway = wholeDaysSince(about.new_design_opted_out_at)
      // Only meaningful for a return, and omitted rather than zeroed when the
      // opt-out predates this bookkeeping.
      if (isReturn && daysAway !== null) eventProps.days_since_opt_out = daysAway
    } else {
      // Days in the CURRENT stint, not since original enrollment.
      const daysIn = wholeDaysSince(about.new_design_stint_started_at)
      if (daysIn !== null) eventProps.time_in_beta_days = daysIn
    }

    safeCapture(
      optingIn ? POSTHOG_EVENTS.INVENTORY_BETA_OPTED_IN : POSTHOG_EVENTS.INVENTORY_BETA_OPTED_OUT,
      eventProps,
    )

    // Opting OUT is a decision, not a "not now". The welcome modal's stage-1
    // condition is `!new_design && !dismissed && !dismissed_final`, which is
    // EXACTLY the state you land in after switching to legacy — so a user who
    // opted in from the sidebar / user-menu (neither of which sets a dismissal)
    // got the "Welcome to Inventory Beta" invite thrown at them the instant
    // they left. Record the choice in the same patch; the explicit
    // "Try Inventory Beta" button is still there whenever they want back in.
    await patchAbout(optingIn
      ? {
          new_design: true,
          new_design_stint: stintNumber,
          new_design_stint_started_at: now,
        }
      : {
          new_design: false,
          new_design_dismissed: true,
          new_design_dismissed_final: true,
          // Start of the gap that `days_since_opt_out` measures on the way back.
          new_design_opted_out_at: now,
        })

    window.location.href = route.fullPath
  }

  /**
   * Persist the FIRST "Maybe later" dismissal. No reload — the user stays on the
   * old design; the stage-2 follow-up prompt appears after a delay.
   */
  const dismissNewDesignPrompt = () => patchAbout({ new_design_dismissed: true })

  /**
   * Persist the SECOND (final) "Maybe later" dismissal. No welcome modal is ever
   * shown again after this.
   */
  const dismissNewDesignPromptFinal = () => patchAbout({ new_design_dismissed_final: true })

  /** Persist that the first-run guided tour was completed/skipped. */
  const markNewDesignTourSeen = () => patchAbout({ new_design_tour_seen: true })

  /**
   * Flip the tour's seen flag — for TESTING the tour, which otherwise runs once
   * per account and can never be seen again. Exposed through a localhost-only
   * control in the header (`DevTourToggle`); the flag itself is the same one
   * production uses, so there is no test-only state to drift.
   */
  const toggleNewDesignTourSeen = () => patchAbout({ new_design_tour_seen: !newDesignTourSeen.value })

  return {
    /** `true` when the user has opted into the new design. */
    isNewDesign,
    /** Convenience inverse for `v-else`-free old-design branches. */
    isOldDesign: computed(() => !isNewDesign.value),
    /** `true` once the first welcome prompt has been dismissed. */
    newDesignPromptDismissed,
    /** `true` once the follow-up prompt has been dismissed (final). */
    newDesignPromptDismissedFinal,
    /** Stage 1: old design, nothing dismissed yet. */
    shouldPromptNewDesign: computed(() =>
      !isNewDesign.value && !newDesignPromptDismissed.value && !newDesignPromptDismissedFinal.value),
    /** Stage 2: old design, first dismissed, follow-up not yet dismissed. */
    shouldPromptNewDesignSecond: computed(() =>
      !isNewDesign.value && newDesignPromptDismissed.value && !newDesignPromptDismissedFinal.value),
    /** `true` once the first-run guided tour has been seen. */
    newDesignTourSeen,
    /** Run the first-run tour: new design, on Inventory, tour not yet seen. */
    shouldRunNewDesignTour: computed(() =>
      isNewDesign.value && !newDesignTourSeen.value),
    /** Persist the design-mode flag (`true`/`false`) then reload into it. */
    setNewDesign,
    /** Persist the first "Maybe later" dismissal (no reload). */
    dismissNewDesignPrompt,
    /** Persist the final "Maybe later" dismissal (no reload). */
    dismissNewDesignPromptFinal,
    /** Persist that the first-run guided tour was completed/skipped. */
    markNewDesignTourSeen,
    /** Dev-only: flip the seen flag so the tour can be replayed. */
    toggleNewDesignTourSeen,
  }
}
